import React from 'react';
import {FieldWithIcon} from "../fieldWithIcon/FieldWithIcon";
import {VehicleDataDTO} from "../../../../../../../../../../api/generated";
import styles from "./VehicleInfoSection.module.css";
import {currencyToIsoCode} from "../../../../../../../../assistant/modal/createAndUpdate/components/util/currencyUtil";

interface Props {
    vehicle: VehicleDataDTO;
    headingRef: React.RefObject<HTMLHeadingElement>;
    derivativeRef: React.RefObject<HTMLParagraphElement>;
    headerHeight: number;
    derivativeHeight: number;
}

export const formatPrice = (vehicle: VehicleDataDTO): string => {
    if (vehicle.price == null) {
        return vehicle.price_label || "Ask for price";
    }
    let price = String(vehicle.price.toFixed(0));

    if (price.includes("£")) {
        price = price.replace("£", "");
    }
    if (price.includes("+VAT")) {
        price = price.replace("+VAT", "");
    }
    const numeric = price.replace(/[^\d]/g, "");

    if (!numeric) {
        return "N/A";
    }

    return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currencyToIsoCode(vehicle.price_currency_code),
        maximumFractionDigits: 0,
    }).format(Number(numeric));
}

export const formatMileage = (mileage: number | string): string => {
    return Number(mileage).toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

export const formatLocationCity = (location_city: string): string => {
    if (location_city.length > 20) {
        return location_city.slice(0, 17) + '...';
    }
    return location_city;
}

export const VehicleInfoSection: React.FC<Props> = ({
                                                        vehicle,
                                                        headingRef,
                                                        derivativeRef,
                                                        headerHeight,
                                                        derivativeHeight
                                                    }) => {
    return (
        <div className={styles.wrapper}>
            <div>
                <div className={styles.headerRow}>
                    <span
                        className={styles.heading}
                        ref={headingRef}
                        style={{minHeight: headerHeight}}
                    >
                        {vehicle.make ?? "No make"} {vehicle.model ?? "No model"}
                    </span>
                    <div className={styles.priceContainer}>
                        <p className={styles.price}>
                            {formatPrice(vehicle)}
                        </p>
                        <p className={styles.vat}>
                            {vehicle.price != null && !vehicle.is_vat_included ? '+VAT' : ''}
                        </p>
                    </div>
                </div>
                <p
                    className={styles.derivative}
                    ref={derivativeRef}
                    style={{minHeight: derivativeHeight}}
                >
                    {vehicle.derivative ?? "No derivative"}
                </p>
                <div className={styles.fields}>
                    {vehicle.year_of_manufacture && (
                        <FieldWithIcon
                            imageUrl='/calendar.svg'
                            fieldValue={vehicle.year_of_manufacture?.toString() ?? "N/A"}
                        />
                    )}
                    {vehicle.mileage && (
                        <FieldWithIcon
                            imageUrl='/mileage.svg'
                            fieldValue={(formatMileage(vehicle.mileage) ?? "N/A ") + " miles"}
                        />
                    )}
                    {vehicle.fuel_type && (
                        <FieldWithIcon
                            imageUrl='/fuel-type.svg'
                            fieldValue={vehicle.fuel_type ?? "N/A"}
                        />
                    )}
                    {vehicle.transmission_type && (
                        <FieldWithIcon
                            imageUrl='/gearbox.svg'
                            fieldValue={vehicle.transmission_type ?? "N/A"}
                        />
                    )}
                    {vehicle.location_city && (
                        <FieldWithIcon
                            imageUrl='/location.svg'
                            fieldValue={formatLocationCity(vehicle.location_city) ?? "N/A"}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
